import axios, {AxiosError, AxiosResponse} from "axios";
import ConfidenceLevels from "../models/ConfidenceLevels";
import { ReviewStatus } from "../models/IReviewer";
import IUser from "../models/Users/IUser";
import IEventDetails from "../models/EventDetails";
import IEventSummary from "../models/EventSummary";
import { AnyEventTypes, AnySubEventTypes } from "../models/EventTypeUtils"
import ISensorIndirection from "../models/SensorIndirection";

const BASE_URL = "/api/v1";

export interface IBackEndResponseModel<T> {
    response: T
    result: boolean
    error: string | null
}

// The backend's 422 handler encodes Pydantic validation errors as a JSON string
// in `error` (see BackEnd/app/main.py's RequestValidationError handler): try to
// pull out the first field-level message so the user sees that instead of raw JSON.
const extractValidationMessage = (errorString: string): string | null => {
    try {
        const parsed = JSON.parse(errorString);
        const detail = parsed?.detail;
        if (Array.isArray(detail) && detail.length > 0 && detail[0]?.msg) {
            return detail[0].msg;
        }
    } catch {
        // Not a JSON-encoded validation error; fall through to the raw message.
    }
    return null;
}

const handleError = (error: AxiosError): IBackEndResponseModel<any> => {
    const response = error.response as AxiosResponse<any, any>;

    if (!response) {
        throw Error("Unknown error has occurred");
    }

    const data: any = response.data;
    if (data?.error) {
        const validationMessage = response.status === 422 ? extractValidationMessage(data.error) : null;
        throw Error(validationMessage ?? (String(response.status) + ": " + data.error));
    }

    throw Error("Unknown error has occurred");
}

const ArtApi = {
    async completeReview(eventId: string, dataScience: boolean, discard: boolean): Promise<IBackEndResponseModel<null>> {

        return axios.patch<IBackEndResponseModel<null>>(BASE_URL + "/reviewer/" + eventId + "?dataScience=" + dataScience,
            { state: discard ? ReviewStatus.discard : ReviewStatus.complete }).then(response => {
                return (response.data)
            }).catch(handleError)
    },
    async createWaterEvent(deviceId: string, regionOfInterestStartDate: Date, endDate: Date, reviewers: string[] | null = null, trainingTime: string | null = null, tags: string[] = []): Promise<IBackEndResponseModel<null>> {
        const startRounded = new Date(regionOfInterestStartDate);
        const endRounded = new Date(endDate);

        startRounded.setUTCHours(0);
        startRounded.setUTCMinutes(0);
        startRounded.setUTCSeconds(0);
        startRounded.setUTCMilliseconds(0);

        endRounded.setUTCHours(23);
        endRounded.setUTCMinutes(59);
        endRounded.setUTCSeconds(59);
        endRounded.setUTCMilliseconds(0);

        return axios.post<IBackEndResponseModel<null>>(BASE_URL + "/water/",
            {
                deviceId,
                regionOfInterestStartDate: startRounded,
                endDate: endRounded,
                reviewers,
                trainingTime,
                tags
            }).then(response => {
                return (response.data)
            }).catch(handleError);
    },
    async createSentreeEvent(deviceId: string, regionOfInterestStartDate: Date, endDate: Date, reviewers: string[] | null = null, trainingTime: string | null = null, tags: string[] = []): Promise<IBackEndResponseModel<null>> {
        const startRounded = new Date(regionOfInterestStartDate);
        const endRounded = new Date(endDate);

        startRounded.setUTCHours(0);
        startRounded.setUTCMinutes(0);
        startRounded.setUTCSeconds(0);
        startRounded.setUTCMilliseconds(0);

        endRounded.setUTCHours(23);
        endRounded.setUTCMinutes(59);
        endRounded.setUTCSeconds(59);
        endRounded.setUTCMilliseconds(0);

        return axios.post<IBackEndResponseModel<null>>(BASE_URL + "/sentree/",
            {
                deviceId,
                regionOfInterestStartDate: startRounded,
                endDate: endRounded,
                reviewers,
                trainingTime,
                tags
            }).then(response => {
                return (response.data)
            }).catch(handleError);
    },
    async getEvents(userOnly: boolean, limit: number, skip: number, taskType: string, getArchived: boolean = false, filterText: string | null = null): Promise<IBackEndResponseModel<IEventSummary[]>> {

        return axios.get<IBackEndResponseModel<IEventSummary[]>>(BASE_URL + "/task/tasks",
            {
                params: { userOnly, limit, skip, getArchived, taskType, filterText }
            }).then(response => {
                return (response.data)
            }).catch(handleError)
    },
    async getUsers(limit: number, skip: number): Promise<IBackEndResponseModel<IUser[]>> {
        return axios.get<IBackEndResponseModel<IUser[]>>(BASE_URL + "/user/",
            {
                params: { limit, skip }
            }).then(response => {
                return (response.data)
            }).catch(handleError)

    },
    async getUser(userId: string): Promise<IBackEndResponseModel<IUser| null>> {
        return axios.get<IBackEndResponseModel<IUser>>(BASE_URL + "/user/"+userId).then(response => {
                return (response.data)
            }).catch(handleError);
    },
    async getUserName(userId: string): Promise<IBackEndResponseModel<string>> {
        return axios.get<IBackEndResponseModel<string>>(BASE_URL + "/user/" + userId + "/name").then(response => {
            return (response.data)
        }).catch(handleError);
    },
    async patchUser(userId: string, fieldKey: string, value: string): Promise<IBackEndResponseModel<null>> {
        return axios.patch<IBackEndResponseModel<null>>(BASE_URL + "/user/" + userId,
        {
            fieldKey, value
        }).then(response => {
            return (response.data)
        }).catch(handleError);
    },
    async addUser(email: string): Promise<IBackEndResponseModel<string>> {
        return axios.post<IBackEndResponseModel<string>>(BASE_URL + "/user/",
        {
            email
        }).then(response => {
            return (response.data)
        }).catch(handleError);
    },
    async getWaterEvent(eventId: string, forceReview: string | null): Promise<IBackEndResponseModel<IEventDetails | null>> {
        return axios.get<IBackEndResponseModel<IEventDetails | null>>(BASE_URL + "/water/" + eventId + (forceReview ? "?forceReview=" + forceReview : ""))
            .then(response => {
                return (response.data)
            }).catch(handleError);
    },
    async getSentreeEvent(eventId: string, forceReview: string | null): Promise<IBackEndResponseModel<IEventDetails | null>> {

        return axios.get<IBackEndResponseModel<IEventDetails | null>>(BASE_URL + "/sentree/" + eventId + (forceReview ? "?forceReview=" + forceReview : ""))
            .then(response => {
                return (response.data)
            }).catch(handleError);
    },
    async getSensorIndirection(coreId: string): Promise<IBackEndResponseModel<ISensorIndirection | null>> {
        return axios.get<IBackEndResponseModel<ISensorIndirection | null>>(BASE_URL + "/rheem/sensorIndirection/" + coreId)
            .then(response => {
                return (response.data)
            }).catch(handleError);
    },
    async getMechanicalTurkNotes(searchTerm: string, numPerPage: number, page: number): Promise<IBackEndResponseModel<any>> {
        const url = `${BASE_URL}/rheem/turk_notes?search_text=${searchTerm}&page=${page}&page_size=${numPerPage}`;
        return axios.get<IBackEndResponseModel<any>>(url).then(response => {
            return (response.data);
        }).catch(handleError);
    },
    async getNotes(coreId: string): Promise<IBackEndResponseModel<string[]>> {
        const url = `${BASE_URL}/rheem/notes/${coreId}`;
        return axios.get<IBackEndResponseModel<string[]>>(url).then(response => {
            return (response.data)
        }).catch(handleError);
    },
    async createNote(coreId: string, note: string, timestamp: Date | number | null): Promise<IBackEndResponseModel<null>> {
        const url = `${BASE_URL}/rheem/notes/${coreId}`;
        const data: any = { note };

        if (timestamp) {
            data.timestamp = timestamp;
        }

        return axios.post<IBackEndResponseModel<null>>(url, data).then(response => {
            return (response.data)
        }).catch(handleError);
    },
    async deleteNote(noteId: string): Promise<IBackEndResponseModel<null>> {
        const url = `${BASE_URL}/rheem/notes/${noteId}`;
        return axios.delete<IBackEndResponseModel<null>>(url).then(response => {
            return (response.data)
        }).catch(handleError);
    },
    async updateNote(noteId: string, note: string, timestamp: Date | null): Promise<IBackEndResponseModel<null>> {
        const url = `${BASE_URL}/rheem/notes/${noteId}`;
        return axios.patch<IBackEndResponseModel<null>>(url, { note, timestamp }).then(response => {
            return (response.data)
        }).catch(handleError);
    },
    async createEvent(
        coreID: string,
        eventType: string,
        eventSubType: string,
        eventDescription: string,
        eventStart: Date,
        eventEnd: Date,
        noPenaltyForMissing: boolean = false,
    ): Promise<IBackEndResponseModel<string>> {
        const url = `${BASE_URL}/rheem/events/`;

        return axios.post<IBackEndResponseModel<string>>(url, {
            coreID,
            eventType,
            eventSubType,
            eventDescription,
            eventStart: eventStart ? Math.round(eventStart.getTime() / 1000) : null,
            eventEnd: eventEnd ? Math.round(eventEnd.getTime() / 1000) : null,
            suspectedRealStart: eventStart ? Math.round(eventStart.getTime() / 1000) : null,
            suspectedRealEnd: eventEnd ? Math.round(eventEnd.getTime() / 1000) : null,
            noPenaltyForMissing
        }).then(response => {
            return (response.data)
        }).catch(handleError);
    },
    async addComment(eventId: string, comment: string): Promise<IBackEndResponseModel<null>> {
        return axios.post<IBackEndResponseModel<null>>(BASE_URL + "/comment/" + eventId,
            {
                comment
            })
            .then(response => {
                return (response.data)
            }).catch(handleError);
    },
    async getMsgpackFile(eventId: string): Promise<Blob> {
        return axios.get<Blob>(BASE_URL + "/msgpack/" + eventId)
            .then(response => {
                return (response.data)
            }).catch(error => {
                if (error.response?.data?.error) {
                    throw Error(String(error.response.status) + ": " + error.response.data.error);
                }
                throw Error("Unknown error has occurred");
            })
    },
    async getWaterChart(eventId: string): Promise<IBackEndResponseModel<any>> {
        return axios.get<IBackEndResponseModel<any>>(BASE_URL + "/water/" + eventId + "/chart").then(response => {
            return (response.data)
        }).catch(handleError);
    },
    async getSentreeChart(eventId: string): Promise<IBackEndResponseModel<any>> {
        return axios.get<IBackEndResponseModel<any>>(BASE_URL + "/sentree/" + eventId + "/chart").then(response => {
            return (response.data)
        }).catch(handleError);
    },
    async getRheemChart(coreId: string, start: string, end: string, resolution: 'd' | 'h' | 'r'): Promise<IBackEndResponseModel<any>> {
        const url = BASE_URL + `/rheem/chart/${coreId}?start=${start}&end=${end}&resolution=${resolution}`

        return axios.get<IBackEndResponseModel<any>>(url).then(response => {
            return (response.data)
        }).catch(handleError);
    },
    async commitApprovedEvents() {
        const url = `${BASE_URL}/rheem/commitApproved/`

        return axios.post<IBackEndResponseModel<any>>(url).then(response => {
            return (response.data)
        }).catch(handleError);
    },
    async getRheemMetadata(macAddress: string): Promise<IBackEndResponseModel<any>> {
        const url = `${BASE_URL}/rheem/metadata/${macAddress}`

        return axios.get<IBackEndResponseModel<any>>(url).then(response => {
            return (response.data)
        }).catch(handleError);
    },
    async getRheemEventCandidates(sort: string, page: number, excludeTypes: string[], pageSize: number | null, coreId: string | undefined, searchTerm: string | undefined, includeArchived: boolean = false): Promise<IBackEndResponseModel<any>> {
        let url = `${BASE_URL}/rheem/events?sort=${sort}&page=${page}&excludeTypes=${excludeTypes.join(',')}&search_text=${searchTerm}&includeArchived=${includeArchived}`
        if (coreId) {
            url += `&coreId=${coreId}`
        }
        if (pageSize) {
            url += `&page_size=${pageSize}`
        }

        return axios.get<IBackEndResponseModel<any>>(url).then(response => {
            return (response.data)
        }).catch(handleError);
    },
    async getRheemIndicators(searchString: string, sort: string, page: number, pageSize: number | null, excludeTypes: string[]): Promise<IBackEndResponseModel<any>> {
        let url = `${BASE_URL}/rheem/indicators?search_text=${searchString}&sort=${sort}&page=${page}&excludeTypes=${excludeTypes.join(',')}`

        if (pageSize) {
            url += `&page_size=${pageSize}`
        }

        return axios.get<IBackEndResponseModel<any>>(url).then(response => {
            return (response.data)
        }).catch(handleError);
    },
    async patchRheemEventCandidate(eventId: string, updates: Record<string, any>): Promise<IBackEndResponseModel<any>> {
        const url = `${BASE_URL}/rheem/events/${eventId}`

        return axios.patch<IBackEndResponseModel<any>>(url, updates).then(response => {
            return (response.data)
        }).catch(handleError);
    },
    async deleteRheemEventCandidate(eventId: string): Promise<IBackEndResponseModel<any>> {
        const url = `${BASE_URL}/rheem/events/${eventId}`
        return axios.delete<IBackEndResponseModel<any>>(url).then(response => {
            return (response.data)
        }).catch(handleError);
    },
    async updateTaggedEvent(
        eventId: string,
        taggedId: string | null,
        start: number,
        end: number,
        comment: string,
        eventType: AnyEventTypes,
        eventSubType: AnySubEventTypes | null,
        eventStage: number | null,
        height: number, user: string | undefined,
        dataScience: boolean,
        confidence: ConfidenceLevels): Promise<IBackEndResponseModel<string>> {
        const eventReviewType = dataScience ? "dataScientestReviewedEvents" : "reviewedEvents"

        return axios.patch<IBackEndResponseModel<string>>(BASE_URL + "/task/" + eventId + "/" + eventReviewType,
            {
                id: taggedId,
                start, end, comment, eventType, eventSubType, eventStage, height, user, confidence
            }
        ).then(response => {
            return (response.data)
        }).catch(handleError)
    },
    async deleteTaggedEvent(eventId: string, recordId: string | null) {
        if (recordId === null) {
            throw Error("Internal error: recordId is null.");
        }
        return axios.delete(BASE_URL + `/task/${eventId}/deleteEvent/${recordId}`);
    },
    async exportTaggedEvents() {
        return axios.get(BASE_URL + "/task/", {headers: {
            media_type:"application/x-zip-compressed"
        }, responseType: 'arraybuffer',})
    },
    async getTasksSlim() {
        return axios.get(BASE_URL + "/task/all")
    },
    async getSingleTask(id: string, include_data: boolean) {
        return axios.get(BASE_URL + "/task/single", {params: {
            id, include_data
        }})
    },
    async deleteTasks(ids: string[]): Promise<IBackEndResponseModel<null>> {
        const result = await axios.post<IBackEndResponseModel<null>>(BASE_URL + "/task/delete", {
            ids
        });
        return result.data;
    }
}

export default ArtApi;
